import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { entitiesApi, Entity } from '@/lib/entities';
import { AppShell } from '@/components/app/AppShell';
import { ConfirmModal } from '@/components/ConfirmModal';
import { CurrencyPicker } from '@/components/CurrencyPicker';
import { EntityPicker } from '@/components/EntityPicker';

export function EntitiesPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const qc = useQueryClient();

  const { data: entities = [], isLoading } = useQuery({
    queryKey: ['entities', wsId],
    queryFn: () => entitiesApi.list(wsId!),
    enabled: !!wsId,
  });

  const tree = useMemo(() => buildTree(entities), [entities]);

  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState<{ name: string; currency: string; parentId: string; eliminateIntercompany: boolean }>({
    name: '', currency: 'USD', parentId: '', eliminateIntercompany: false,
  });

  const create = useMutation({
    mutationFn: () => entitiesApi.create(wsId!, {
      name: draft.name.trim(),
      currency: draft.currency.trim().toUpperCase(),
      parentId: draft.parentId || null,
      eliminateIntercompany: draft.eliminateIntercompany,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entities', wsId] });
      setDraft({ name: '', currency: 'USD', parentId: '', eliminateIntercompany: false });
      setShowForm(false);
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => entitiesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['entities', wsId] }),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof entitiesApi.update>[1] }) =>
      entitiesApi.update(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['entities', wsId] }),
  });

  // Pending delete handled via ConfirmModal at the bottom of the page.
  const [pendingDelete, setPendingDelete] = useState<Entity | null>(null);

  return (
    <AppShell title={t('entities.title')} subtitle={t('entities.subtitle')}>
      <main className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-semibold text-foreground tracking-tight">{t('entities.title')}</h1>
            <p className="mt-1 text-muted-foreground max-w-2xl">{t('entities.subtitle')}</p>
          </div>
          <button
            onClick={() => setShowForm((s) => !s)}
            className="h-9 px-3.5 inline-flex items-center rounded-md bg-foreground text-background text-[12.5px] font-semibold hover:bg-foreground/90 transition-colors"
          >
            {t('entities.new')}
          </button>
        </div>

        {showForm && (
          <div className="rounded-md border border-border bg-card p-5 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-1">{t('entities.name')}</span>
                <input
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm"
                />
              </label>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-1">{t('entities.currency')}</span>
                <CurrencyPicker
                  value={draft.currency}
                  onChange={(v) => setDraft((d) => ({ ...d, currency: v }))}
                  className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm"
                />
              </label>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-1">{t('entities.parent')}</span>
                <EntityPicker
                  wsId={wsId!}
                  value={draft.parentId}
                  onChange={(id) => setDraft((d) => ({ ...d, parentId: id }))}
                  includeNone
                  noneLabel={t('entities.parent_none')}
                />
              </label>
              <label className="flex items-start gap-2 sm:col-span-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.eliminateIntercompany}
                  onChange={(e) => setDraft((d) => ({ ...d, eliminateIntercompany: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 rounded border-border"
                />
                <span className="text-xs text-ink-700">
                  {t('entities.eliminate_ic', { defaultValue: 'Eliminate intercompany transactions in consolidation' })}
                  <span className="block text-[11px] text-muted-foreground">
                    {t('entities.eliminate_ic_hint', { defaultValue: 'Drop balances flagged as intra-group when this entity is consolidated.' })}
                  </span>
                </span>
              </label>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => setShowForm(false)}
                className="text-xs text-muted-foreground hover:text-foreground px-2 py-1"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => create.mutate()}
                disabled={create.isPending || draft.name.trim().length === 0 || draft.currency.length !== 3}
                className="text-xs font-semibold px-3 py-1.5 rounded-md bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60"
              >
                {t('entities.new')}
              </button>
            </div>
            {create.error && (
              <p className="text-xs text-rose-700">{(create.error as Error).message}</p>
            )}
          </div>
        )}

        {isLoading ? (
          <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
        ) : entities.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-card p-12 text-center">
            <p className="text-foreground font-medium">{t('entities.empty')}</p>
          </div>
        ) : (
          <div className="rounded-md border border-border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border bg-foreground/[0.02]">
                  <th className="py-2 px-4 text-[10px] uppercase tracking-[0.16em] font-medium">{t('entities.name')}</th>
                  <th className="py-2 px-3 text-[10px] uppercase tracking-[0.16em] font-medium">{t('entities.currency')}</th>
                  <th className="py-2 px-3 text-[10px] uppercase tracking-[0.16em] font-medium text-right">{t('entities.ownership')}</th>
                  <th className="py-2 px-3 text-[10px] uppercase tracking-[0.16em] font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {tree.map((node) => (
                  <TreeRow
                    key={node.entity.id}
                    node={node}
                    onDelete={(id) => {
                      const ent = entities.find((e) => e.id === id);
                      if (ent) setPendingDelete(ent);
                    }}
                    onSave={(id, patch) => updateMut.mutateAsync({ id, patch })}
                    t={t}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <ConfirmModal
        open={pendingDelete !== null}
        title={t('workbook.delete_confirm')}
        body={pendingDelete?.name}
        confirmLabel={t('common.delete')}
        destructive
        busy={del.isPending}
        onConfirm={() => {
          if (pendingDelete) {
            del.mutate(pendingDelete.id, { onSettled: () => setPendingDelete(null) });
          }
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </AppShell>
  );
}

interface TreeNode {
  entity: Entity;
  depth: number;
}

function buildTree(entities: Entity[]): TreeNode[] {
  const byParent = new Map<string | null, Entity[]>();
  for (const e of entities) {
    const k = e.parentId;
    if (!byParent.has(k)) byParent.set(k, []);
    byParent.get(k)!.push(e);
  }
  const out: TreeNode[] = [];
  const walk = (parentId: string | null, depth: number) => {
    const kids = byParent.get(parentId) ?? [];
    for (const e of kids.sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0) || a.name.localeCompare(b.name))) {
      out.push({ entity: e, depth });
      walk(e.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

function TreeRow({
  node, onDelete, onSave, t,
}: {
  node: TreeNode;
  onDelete: (id: string) => void;
  onSave: (id: string, patch: Parameters<typeof entitiesApi.update>[1]) => Promise<unknown>;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const { entity, depth } = node;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    name: entity.name,
    currency: entity.currency,
    ownership: String(Math.round(entity.ownershipPct * 1000) / 10),  // shown as 100.0
    eliminateIntercompany: entity.eliminateIntercompany,
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await onSave(entity.id, {
        name: draft.name.trim() || entity.name,
        currency: draft.currency.trim().toUpperCase(),
        ownershipPct: Math.max(0.00001, Math.min(1, Number(draft.ownership) / 100)),
        eliminateIntercompany: draft.eliminateIntercompany,
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    const inputCls = 'rounded-md border border-border bg-white px-2 py-1 text-xs';
    return (
      <tr className="bg-foreground/[0.03]">
        <td className="py-2 px-4">
          <div className="flex items-center gap-2" style={{ paddingLeft: `${depth * 16}px` }}>
            {depth > 0 && <span className="text-muted-foreground">└</span>}
            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className={`${inputCls} w-full max-w-[260px]`} />
          </div>
        </td>
        <td className="py-2 px-3">
          <CurrencyPicker
            value={draft.currency}
            onChange={(v) => setDraft({ ...draft, currency: v })}
            className={`${inputCls} w-32`}
          />
        </td>
        <td className="py-2 px-3 text-right">
          <input value={draft.ownership} onChange={(e) => setDraft({ ...draft, ownership: e.target.value })} className={`num ${inputCls} w-20 text-right`} inputMode="decimal" />
        </td>
        <td className="py-2 px-3 text-right whitespace-nowrap">
          <label className="inline-flex items-center gap-1 text-[11px] text-ink-600 mr-2 cursor-pointer" title={t('entities.eliminate_ic', { defaultValue: 'Eliminate intercompany transactions in consolidation' })}>
            <input
              type="checkbox"
              checked={draft.eliminateIntercompany}
              onChange={(e) => setDraft({ ...draft, eliminateIntercompany: e.target.checked })}
              className="h-3.5 w-3.5 rounded border-border"
            />
            {t('entities.eliminate_ic_short', { defaultValue: 'Elim. IC' })}
          </label>
          <button onClick={() => setEditing(false)} disabled={saving} className="text-[11px] text-muted-foreground hover:text-foreground px-2 py-1">
            {t('common.cancel')}
          </button>
          <button onClick={save} disabled={saving} className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-800 px-2 py-1 disabled:opacity-60">
            {saving ? t('settings.saving') : t('common.save')}
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="hover:bg-foreground/[0.02]">
      <td className="py-2 px-4">
        <div className="flex items-center gap-2" style={{ paddingLeft: `${depth * 16}px` }}>
          {depth > 0 && <span className="text-muted-foreground">└</span>}
          <span className="font-medium text-foreground">{entity.name}</span>
          {entity.isPrimary && (
            <span className="num text-[9px] uppercase tracking-wide text-emerald-700 font-semibold">
              ✦ {t('entities.primary')}
            </span>
          )}
        </div>
      </td>
      <td className="num text-foreground/80 py-2 px-3">{entity.currency}</td>
      <td className="num text-right text-foreground/80 py-2 px-3">{(entity.ownershipPct * 100).toFixed(1)}%</td>
      <td className="py-2 px-3 text-right whitespace-nowrap">
        <button
          onClick={() => setEditing(true)}
          className="text-[11px] text-muted-foreground hover:text-foreground px-2 py-1 rounded-md"
        >
          {t('common.edit')}
        </button>
        {!entity.isPrimary && (
          <button
            onClick={() => onDelete(entity.id)}
            className="text-[11px] text-muted-foreground hover:text-rose-700 px-2 py-1 rounded-md"
          >
            {t('workbook.delete')}
          </button>
        )}
      </td>
    </tr>
  );
}
